import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "Results", href: "#stats" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-2xl bg-[#050505]/70 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex h-16 md:h-20 items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => handleNav("#hero")}
          className="flex items-center gap-2 group"
        >
          <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[#FF7B00] to-[#FF9933] text-black font-display font-black">
            M
            <span className="absolute -right-1 -bottom-1 h-2.5 w-2.5 rounded-sm bg-[#FFB347] border border-black" />
          </span>
          <span className="font-display text-sm md:text-base font-bold tracking-tight text-white">
            Mature<span className="text-[#FF7B00]">.</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => handleNav(l.href)}
              className="text-sm font-medium text-zinc-300 hover:text-[#FF7B00] transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-[#FF7B00] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <button
          data-testid="nav-cta-get-started"
          onClick={() => handleNav("#contact")}
          className="hidden md:inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FF7B00] to-[#FF9933] px-5 py-2.5 text-sm font-bold text-black shadow-[0_0_20px_rgba(255,123,0,0.3)] hover:shadow-[0_0_32px_rgba(255,123,0,0.55)] transition-all hover:scale-[1.03]"
        >
          Get Started <ArrowUpRight className="h-4 w-4" strokeWidth={2.25} />
        </button>

        <button
          data-testid="nav-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/10 text-white"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile */}
      <div
        data-testid="mobile-menu"
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"
        } bg-[#050505]/95 backdrop-blur-xl border-b border-white/5`}
      >
        <div className="px-6 py-6 flex flex-col gap-1">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => handleNav(l.href)}
              className="flex items-center justify-between py-3 border-b border-white/5 text-left text-zinc-200 hover:text-[#FF7B00]"
            >
              <span className="font-display text-xl">{l.label}</span>
              <ArrowUpRight className="h-4 w-4 opacity-60" />
            </button>
          ))}
          <button
            data-testid="mobile-cta-get-started"
            onClick={() => handleNav("#contact")}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#FF7B00] to-[#FF9933] px-5 py-3 text-sm font-bold text-black"
          >
            Get Started <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
